/** Stage-specific rich rendering of an agent's output_json. */
import { FileText, ScanLine } from 'lucide-react';
import { Stat } from './StageRichBits';
import { RichDocumentAssistantBody } from './RichDocumentAssistantBody';
import { RichDocumentBody } from './RichDocumentBody';
import { RichPartiesBody } from './RichPartiesBody';
import { RichClauseBody } from './RichClauseBody';
import { RichComplianceBody } from './RichComplianceBody';
import { RichSignatureBody } from './RichSignatureBody';
import { RichGate1Body, RichGate2Body } from './RichGateBody';
import { RichGateFinalBody } from './RichGateFinalBody';
import { RichBootstrapBody } from './RichBootstrapBody';
import type { PausedStage } from '@/types/enums';

interface Props {
  stage: PausedStage;
  output: Record<string, unknown>;
  /** Required by the document stage so view-only translations land in
   *  the per-job activity feed. Reserved for future per-stage hooks. */
  jobId?: string;
  /** Fired after a view-only action (translate, etc.) succeeds so the
   *  parent can refresh the JobCommentsPanel activity feed. */
  onActivity?: () => void;
}

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asText = (v: unknown): string => (typeof v === 'string' ? v : '');

export function StageRichBody({ stage, output, jobId, onActivity }: Props) {
  switch (stage) {
    case 'document_assistant':
      return <RichDocumentAssistantBody output={output} />;
    case 'document':
      return (
        <RichDocumentBody
          output={output}
          jobId={jobId ?? null}
          onTranslated={onActivity}
        />
      );
    case 'parties':
      return <RichPartiesBody output={output} />;
    case 'clause':
      return <RichClauseBody output={output} />;
    case 'gate_1':
      return <RichGate1Body output={output} />;
    case 'compliance':
      return <RichComplianceBody output={output} />;
    case 'signature':
      return <RichSignatureBody output={output} />;
    case 'gate_2':
      return <RichGate2Body output={output} />;
    case 'gate':
      return <RichGateFinalBody output={output} />;
    case 'orchestrator_bootstrap':
      return <RichBootstrapBody output={output} />;

    case 'orchestrator': {
      // Legacy 7-stage flow — kept so jobs paused at the old "orchestrator"
      // stage at the time of the 9-stage rewrite still render cleanly.
      const planned = asArray(output.planned_agents)
        .map((a) => (typeof a === 'string' ? a : ''))
        .filter(Boolean);
      const obs = asArray(output.preliminary_observations)
        .map((o) => (typeof o === 'string' ? o : ''))
        .filter(Boolean);
      const notes = asText(output.notes);
      const klass = asText(output.document_class) || 'unknown';
      const complexity = asText(output.estimated_complexity) || 'medium';
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <Stat icon={FileText} label="Document class" value={klass} />
            <Stat icon={ScanLine} label="Estimated complexity" value={complexity} />
          </div>
          {notes && (
            <div className="rounded border bg-muted/50 p-3 text-sm">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Orchestrator notes
              </p>
              <p>{notes}</p>
            </div>
          )}
          {planned.length > 0 && (
            <div className="text-sm">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Planned agent sequence
              </p>
              <ol className="list-decimal list-inside text-xs">
                {planned.map((a) => (
                  <li key={a} className="capitalize">{a.replace(/_/g, ' ')}</li>
                ))}
              </ol>
            </div>
          )}
          {obs.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30">
              <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">
                Preliminary observations ({obs.length})
              </p>
              <ul className="list-disc list-inside text-xs text-amber-700 dark:text-amber-200">
                {obs.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
        </div>
      );
    }
  }
}
